import React, { useCallback, useMemo, useRef } from 'react'
import { useSetState } from 'ahooks'
import { Button, message, Upload } from 'antd'
import {
  SActionBtn,
  SDrawerOrModal,
  SFuncModalFormRef,
  SLowcodeList,
  SMessage,
  STableActions,
} from 'shj-components'
import { getEnums, httpOrgList } from '@/services/homeService.ts'
import {
  getBrandList,
  getProductCommonPage,
  getSpecialCodesList, priceStandardRateBatchUpdateDate,
} from '@/services/product.ts'
import { apiData2Table } from '@/utils/data.ts'
import { getConfig } from './config.tsx'
import AddOrEditConfigure from './addOrEdit'
import { httpDictServList } from '@/services'
import DebounceSearchSelect from '@/pages/baseProduct/components/DebounceSearchSelect'
import { fileDownload } from '@/utils/download.ts'
import EditTimeModal from "./EditTimeModal";
import LogsModal from "./LogsModal";
import { METHODS } from "@/api";

const PriceStrategyTable = props => {
  const { current, apiUrlObj, businessId } = props
  const listRef = useRef()
  const editRef = useRef<SFuncModalFormRef>({ onFinish: undefined })
  const [state, setState] = useSetState<any>({
    exportLoading: false,
    downloadLoading: false,
    importLoading: false,
    visible: false,
    loading: false,
    record: {},
    initialCompleted: false,
    chooseDisabled: false,
    selectedRowKeys: [],
    dataList: [],
    editType: '',
    productId: '',
    totalCount: 0,
  })

  const fetchList = ({ current, pageSize, query }) => {
    return new Promise((resolve, reject) => {
      apiUrlObj
        .getPage({
          params: { pageId: current, pageSize },
          data: {
            ...query,
            [apiUrlObj.searchKey]: props[apiUrlObj.searchKey],
            queryCompanyName: true,
            queryProductName: true,
            queryPriceType: true,
            querySource: true,
            queryBrandName: true,
            id: state.initialCompleted ? undefined : businessId,
          },
        })
        .then(res => {
          resolve(apiData2Table(res))
          setState({dataList: res.list, totalCount: res.count})
        })
        .catch(reject)
    })
  }

  const onAddOrEdit = (record = {}) => {
    setState({ visible: true, record: { ...record } })
  }

  const onDel = (id: string) => {
    SMessage.confirm({
      onOk: () => {
        return apiUrlObj.deleteById(id).then((res: any) => {
          message.success(res.message || '操作成功')
          listRef.current?.reload()
        })
      },
    })
  }

  const onBatchDelete = ({ selectedRowKeys }) => {
    SMessage.confirm({
      content: `是否确认删除所选数据`,
      onOk: () => {
        return apiUrlObj.batchDelete({ ids: selectedRowKeys }).then((res: any) => {
          message.success(res.message || '操作成功')
          listRef.current?.reload()
        })
      },
    })
  }

  const onSubmit = () => {
    editRef.current.onFinish?.()
  }

  const onCancel = () => {
    setState({ visible: false })
  }

  const onImport = (upload: any) => {
    setState({ importLoading: true })
    const { file, onSuccess, onError } = upload
    const data = new FormData()
    data.append('file', file)
    return apiUrlObj
      .importFile(data, current.key)
      .then((res: any) => {
        listRef.current && listRef.current.reload()
        onSuccess && onSuccess()
        message.success(res.message || '导入成功')
      })
      .catch(onError)
      .finally(() => setState({ importLoading: false }))
  }

  const onDownload = () => {
    setState({ downloadLoading: true })
    fileDownload(apiUrlObj.download + `${apiUrlObj.searchKey ? current.key : ''}`)
      .then(() => setState({ downloadLoading: false }))
      .catch(() => setState({ downloadLoading: false }))
  }

  const onExport = () => {
    setState({ exportLoading: true })
    const data = {
      ...listRef.current?.getSearch?.()?.getFieldsValue?.() || {},
      sourceType: 'STANDARD',
      queryCompanyName: true,
      queryProductName: true,
      queryPriceType: true,
      querySource: true,
      queryBrandName: true,
      id: state.initialCompleted ? undefined : businessId,
    }
    fileDownload(apiUrlObj.export, { data, method: METHODS.POST, error: false })
      .then(() => setState({ exportLoading: false }))
      .catch(async (err) => {
        if (err instanceof Blob || Object.prototype.toString.call(err) === '[object Blob]') {
          try {
            const text = await err.text();
            const jsonData = JSON.parse(text);
            message.error(jsonData.message);
          } catch (error) {
            return err;
          }
        }
        setState({ exportLoading: false })
      })
  }

  const getStatusOptions = async () => {
    const companyList: any = await httpOrgList()
    const priceTypeList = await httpDictServList({ parentKey: 'MpmsPriceType' })
    const brandList: any = await getBrandList()
    const specialCodeList: any = await getSpecialCodesList({
      data: {},
    })
    const alertTypeEnum = await getEnums('plus-product', 'com.shj.saas.plus.product.enums.PriceWarningAlertTypeEnum')
    return Promise.resolve({
      companyList: [{ name: '通用', id: '0' }, ...companyList],
      priceTypeList,
      brandList: [{ name: '通用', id: '0' }, ...brandList],
      specialCodeList,
      alertTypeEnum,
    })
  }

  const allSelect = () => {
    if (state.chooseDisabled) {
      setState({chooseDisabled: false})
      listRef?.current?.clearSelectedRowKeys()
    } else {
      const ids: any = state.dataList?.map((item: any) => item.id)
      setState({chooseDisabled: true, selectedRowKeys: ids})
    }
  }
  const onBatchChangeStartTime = () => {
    onCheckDate('start')
  }

  const onBatchChangeEndTime = () => {
    onCheckDate('end')
  }

  const onCheckDate = (editType = 'start') => {
      setState({ EDIT_DATE: true, editType })
  }

  const onLogs = (record) => {
    setState({ LOGS: true, record })
  }

  const fixedParams = useMemo(() => ({
    prodTypes: ['PRODUCT_PACKAGE'],
    enabled: undefined,
  }), []);

  const handleProductChange = useCallback((form, name, v) => {
    form.setFieldValue(name, v);
    setState({ productId: v });
  }, []);

  const query = useMemo(() =>
      state.productId ? { id: state.productId } : undefined
    , [state.productId]);

  const MemoizedDebounceSearchSelect = React.memo(DebounceSearchSelect, (prevProps, nextProps) => {
    return (
      prevProps.disabled === nextProps.disabled &&
      prevProps.value === nextProps.value &&
      JSON.stringify(prevProps.query) === JSON.stringify(nextProps.query)
    );
  });

  const searchSelectRender = useCallback(({ name, form, disabled }) => {
    return (
      <MemoizedDebounceSearchSelect
        allowClear
        label={'产品'}
        fetchOptions={getProductCommonPage}
        fixedParams={fixedParams}
        onChange={(v) => handleProductChange(form, name, v)}
        fieldNames={{ label: 'prodName' }}
        style={{ width: '100%' }}
        query={query}
        disabled={disabled}
      />
    );
  }, [fixedParams, handleProductChange, query]);

  const extensions = {
    onSearchReset: () => { setState({initialCompleted: true})},
    request: { fetchList, getStatusOptions },
    events: {
      onAdd: () => onAddOrEdit(),
      onBatchDelete,
      allSelect,
      onBatchChangeStartTime,
      onBatchChangeEndTime,

    },
    preserveSelectedRowKeys: true,
    rowSelectedTotal: state.chooseDisabled ? state.totalCount : '',
    getCheckboxProps: () => ({
      disabled: state.chooseDisabled,
    }),
    status: {
      disabled: () => state.chooseDisabled,
    },
    selectedRowKeys: state.selectedRowKeys,
    onSelectedRowChange: (keys) => {
      setState({ selectedRowKeys: keys })
    },
    customRender: {
      jsonRender(v: any) {
        const list = v && JSON.parse(v)?.shops
        return list?.map(item => item.name)?.join(', ')
      },
      actionRender(_: null, record: Record<string, any>) {
        return (
          <STableActions>
            <SActionBtn onClick={() => onAddOrEdit(record)}>编辑</SActionBtn>
            {props.sourceType === 'STANDARD' && <SActionBtn onClick={() => onLogs(record)}>调整记录</SActionBtn>}
            <SActionBtn danger onClick={() => onDel(record.id)}>
              删除
            </SActionBtn>
          </STableActions>
        )
      },
      importRender: () => {
        return (
          <Upload listType="text" customRequest={onImport} itemRender={() => null}>
            <Button
              type={'primary'}
              loading={state.importLoading}
              disabled={state.importLoading}
              // icon={<UploadOutlined />}
            >
              批量导入
            </Button>
          </Upload>
        )
      },
      downloadRender: () => {
        return (
          <Button
            // icon={<DownloadOutlined />}
            onClick={onDownload}
            disabled={state.downloadLoading}
            loading={state.downloadLoading}
          >
            下载导入模板
          </Button>
        )
      },
      exportRender: () => {
        return (
          <Button
            type={'primary'}
            // icon={<DownloadOutlined />}
            onClick={onExport}
            disabled={state.exportLoading}
            loading={state.exportLoading}
          >
            导出
          </Button>
        )
      },
      searchSelectRender,
    },
  }

  const onReloadList = () => {
    listRef.current?.reload?.()
    setState({chooseDisabled: false, selectedRowKeys: []});
    listRef?.current?.clearSelectedRowKeys()
  }

  const onCloseModal = (key) => {
    setState({ [key]: false })
  }

  const getQuery = () => {
    const query = state.chooseDisabled ? { query: listRef.current?.getSearch?.()?.getFieldsValue?.() } : { ids: state.selectedRowKeys };
    if (state.chooseDisabled && !state.initialCompleted && businessId) {
      query.ids = [businessId]
    }
    return query
  }

  return (
    <div>
      <SLowcodeList
        ref={listRef}
        searchMode={'group'}
        config={getConfig(current)}
        {...extensions}
      />
      {state.visible && <SDrawerOrModal
        mode={'modal'}
        width={500}
        onCancel={onCancel}
        onOk={onSubmit}
        open={state.visible}
        loading={state.loading}
        title={(state.record.id ? '编辑' : '新增') + '配置'}
      >
        <AddOrEditConfigure
          fn={listRef.current?.reload}
          onClose={onCancel}
          apiObj={apiUrlObj}
          editRef={editRef}
          current={current}
          setLoading={(loading: boolean) => setState({ loading })}
          record={state.record}
        />
      </SDrawerOrModal>}
      {state['EDIT_DATE'] && <EditTimeModal
          open={state['EDIT_DATE']}
          editType={state.editType}
          record={state.record}
          query={getQuery()}
          onReload={onReloadList}
          onClose={() => onCloseModal('EDIT_DATE')}
      />}
      {state['LOGS'] && <LogsModal
          open={state['LOGS']}
          record={state.record}
          onClose={() => onCloseModal('LOGS')}
      />}
    </div>
  )
}

export default PriceStrategyTable
